import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MapPin, Check, AlertCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BOARD_ICONS, ICON_PREFIX, iconUrl, BoardIcon } from "@/lib/boardIcons";

// Legacy Unicode emoji set — kept as a fallback tab for users who prefer
// the OS emoji font. The primary picker is the 3D icon pack below.
const EMOJI_OPTIONS = [
  "🏠", "🏡", "🏢", "🏘️", "🏙️", "🏛️", "🏰", "🗼",
  "🏗️", "🏖️", "🏔️", "🌳", "🌴", "🌲", "🌊", "🌇",
  "📍", "🗺️", "🧭", "🎯", "📌", "🔖", "⭐", "🌟",
  "💼", "📊", "📈", "💰", "💶", "💵", "💳", "🏦",
  "✨", "🔥", "💎", "💡", "🚀", "⚡", "🎨",
];

const BoardDialog = ({ open, onOpenChange, editing, onSaved }) => {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [emoji, setEmoji] = useState("🏠");
  const [saving, setSaving] = useState(false);
  // location validation state: null | "checking" | "ok" | "error"
  const [locStatus, setLocStatus] = useState(null);
  const [locError, setLocError] = useState("");

  useEffect(() => {
    if (editing) {
      setName(editing.name || "");
      setLocation(editing.location || "");
      setDescription(editing.description || "");
      setEmoji(editing.emoji || "🏠");
      setLocStatus(editing.location ? "ok" : null);
    } else {
      setName(""); setLocation(""); setDescription(""); setEmoji("🏠"); setLocStatus(null);
    }
    setLocError("");
  }, [editing, open]);

  // Debounced validation while typing (via LocationIQ through geocode-preview endpoint... we reuse map preview).
  // Simple check: consider valid when non-empty; server will do the authoritative geocode.
  useEffect(() => {
    if (!location.trim()) { setLocStatus(null); return; }
    setLocStatus("checking");
    const h = setTimeout(() => setLocStatus("ok"), 300);
    return () => clearTimeout(h);
  }, [location]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Il nome è obbligatorio"); return; }
    if (!location.trim()) { toast.error("La località è obbligatoria"); return; }
    setSaving(true);
    setLocError("");
    try {
      const payload = { name: name.trim(), location: location.trim(), description, emoji };
      if (editing) {
        await api.put(`/boards/${editing.id}`, payload);
        toast.success("Scheda aggiornata");
      } else {
        await api.post("/boards", payload);
        toast.success("Scheda creata");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      const msg = err.response?.data?.detail || "Errore salvataggio";
      // If it's a location-specific error, show inline as well
      if (msg.toLowerCase().includes("localit")) {
        setLocStatus("error");
        setLocError(msg);
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const LocationStatusIcon = () => {
    if (locStatus === "checking") return <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />;
    if (locStatus === "ok") return <Check className="w-4 h-4 text-emerald-600" />;
    if (locStatus === "error") return <AlertCircle className="w-4 h-4 text-rose-600" />;
    return <MapPin className="w-4 h-4 text-slate-400" />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl border border-slate-200 bg-white max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-2xl tracking-tight">
            {editing ? "Modifica scheda" : "Nuova scheda di mappatura"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Icona</Label>
            <div className="flex items-center gap-3 mt-1">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center shrink-0 border border-slate-200" data-testid="bd-emoji-preview">
                <BoardIcon value={emoji} size={44} />
              </div>
              <p className="text-[11px] text-slate-500">Scegli un'icona 3D (o un emoji legacy) che rappresenti la scheda. Apparirà nella lista.</p>
            </div>

            {/* Primary picker: 3D icon pack shipped in /public/board-icons */}
            <div
              className="grid grid-cols-8 gap-1.5 mt-3 p-2 rounded-xl bg-slate-50 border border-slate-100 max-h-64 overflow-y-auto"
              data-testid="bd-icon-grid"
            >
              {BOARD_ICONS.map((it) => {
                const value = `${ICON_PREFIX}${it.slug}`;
                const active = emoji === value;
                return (
                  <button
                    key={it.slug}
                    type="button"
                    onClick={() => setEmoji(value)}
                    data-testid={`bd-icon-${it.slug}`}
                    title={it.label}
                    className={`h-11 w-11 rounded-xl flex items-center justify-center transition-all hover:bg-white hover:scale-110 ${
                      active ? "bg-white ring-2 ring-[#007AFF] shadow-sm" : ""
                    }`}
                    aria-label={`Scegli icona ${it.label}`}
                  >
                    <img
                      src={iconUrl(it.slug)}
                      alt={it.label}
                      className="w-9 h-9 object-contain pointer-events-none"
                      loading="lazy"
                    />
                  </button>
                );
              })}
            </div>

            {/* Fallback: legacy Unicode emoji */}
            <details className="mt-3 group">
              <summary className="text-[11px] uppercase tracking-widest text-slate-500 cursor-pointer select-none hover:text-slate-700 flex items-center gap-1">
                <span className="group-open:rotate-90 inline-block transition-transform">›</span>
                Emoji legacy
              </summary>
              <div
                className="grid grid-cols-8 gap-1.5 mt-2 p-2 rounded-xl bg-slate-50 border border-slate-100 max-h-40 overflow-y-auto"
                data-testid="bd-emoji-grid"
              >
                {EMOJI_OPTIONS.map((e, i) => (
                  <button
                    key={`${e}-${i}`}
                    type="button"
                    onClick={() => setEmoji(e)}
                    data-testid={`bd-emoji-${e}`}
                    className={`h-9 w-9 rounded-lg flex items-center justify-center text-xl transition-all hover:bg-white hover:scale-110 ${
                      emoji === e ? "bg-white ring-2 ring-[#007AFF] shadow-sm" : ""
                    }`}
                    aria-label={`Scegli emoji ${e}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </details>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Nome scheda *</Label>
            <Input
              data-testid="bd-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Es. Focus trilocali sotto media"
              className="rounded-xl mt-1 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF]"
              required
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider flex items-center justify-between">
              <span>Località *</span>
              <span className="text-[10px] text-slate-500 font-normal normal-case tracking-normal">Deve corrispondere a un luogo reale</span>
            </Label>
            <div className="relative mt-1">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <LocationStatusIcon />
              </div>
              <Input
                data-testid="bd-location"
                value={location}
                onChange={(e) => { setLocation(e.target.value); setLocError(""); }}
                placeholder="Es. Roma, Milano Centro, Firenze, Napoli Vomero…"
                className={`rounded-xl bg-slate-100 border-0 pl-9 focus-visible:ring-2 ${
                  locStatus === "error" ? "focus-visible:ring-rose-500 ring-2 ring-rose-300" : "focus-visible:ring-[#007AFF]"
                }`}
                required
              />
            </div>
            {locError && (
              <p className="text-[11px] text-rose-600 mt-1" data-testid="bd-location-error">{locError}</p>
            )}
            <p className="text-[11px] text-slate-500 mt-1">
              Sarà usata per centrare la mappa di sfondo della card. Se la località non ha corrispondenza geografica, la creazione verrà rifiutata.
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Descrizione</Label>
            <Textarea
              data-testid="bd-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Scopo, target, target… (opzionale)"
              className="rounded-xl mt-1 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF] min-h-[80px]"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" data-testid="bd-save" disabled={saving || !name.trim() || !location.trim()} className="rounded-full bg-[#007AFF] hover:bg-[#0060df]">
              {saving ? "Salvataggio…" : editing ? "Aggiorna" : "Crea scheda"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BoardDialog;
